import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { api } from '../services/authenticatedApi';
import { analytics } from '../services/analytics/posthog';
import { logger } from '../utils/logger';

export interface UseZipUploadOptions {
  socketId?: string;
}

export interface UseZipUploadReturn {
  selectedFile: File | null;
  isUploading: boolean;
  uploadProgress: number;
  uploadStatus: string;
  error: string | null;
  setError: (error: string | null) => void;
  handleFileSelect: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleDrop: (event: React.DragEvent) => void;
  handleDragOver: (event: React.DragEvent) => void;
  handleUpload: () => Promise<void>;
  handleCancelUpload: () => void;
  clearSelectedFile: () => void;
}

/**
 * Validate an upload file for type and size.
 * Returns an error message string if invalid, or null if valid.
 */
export const validateUploadFile = (file: File, maxUploadSizeMB: number): string | null => {
  const validExtensions = [
    '.zip',
    '.tar',
    '.tar.gz',
    '.tgz',
    '.tar.bz2',
    '.tbz2',
    '.rar',
    '.7z',
    '.gz',
    '.bz2',
  ];
  const fileName = file.name.toLowerCase();
  const isValidArchive = validExtensions.some((ext) => fileName.endsWith(ext));

  if (!isValidArchive) {
    return 'Please select a valid archive file (ZIP, TAR, TAR.GZ, RAR, 7Z, etc.)';
  }

  if (file.size > maxUploadSizeMB * 1024 * 1024) {
    return `File size must be less than ${maxUploadSizeMB}MB`;
  }

  return null;
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export function useZipUpload(
  maxUploadSizeMB: number,
  options: UseZipUploadOptions,
): UseZipUploadReturn {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    setError(null);

    if (!file) return;

    const validationError = validateUploadFile(file, maxUploadSizeMB);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSelectedFile(file);
  };

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    const file = event.dataTransfer.files[0];

    if (file) {
      setError(null);

      const validationError = validateUploadFile(file, maxUploadSizeMB);
      if (validationError) {
        setError(validationError);
        return;
      }

      setSelectedFile(file);
    }
  };

  /**
   * Direct upload to MinIO handler
   * 1. Request presigned URL from server
   * 2. Upload directly to MinIO using presigned URL
   * 3. Confirm upload with server
   * 4. Start analysis
   */
  const handleUpload = async () => {
    if (!selectedFile) return;

    setIsUploading(true);
    setError(null);
    setUploadProgress(0);
    setUploadStatus('Preparing upload...');
    analytics.analysisStarted(selectedFile.name, 'local');

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    try {
      setUploadProgress(5);

      const initiateResponse = await api.post(
        '/repositories/uploads/initiate',
        {
          filename: selectedFile.name,
          size: selectedFile.size,
          contentType: selectedFile.type || 'application/octet-stream',
        },
        { signal },
      );

      const { uploadId, uploadUrl } = initiateResponse.data;

      setUploadProgress(10);
      setUploadStatus('Uploading file...');

      await new Promise<void>((resolve, reject) => {
        const xhr = new XMLHttpRequest();

        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percentComplete = 10 + Math.round((event.loaded / event.total) * 80);
            setUploadProgress(percentComplete);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed: Network error'));
        });

        xhr.addEventListener('abort', () => {
          reject(new Error('Upload cancelled'));
        });

        signal.addEventListener('abort', () => {
          xhr.abort();
        });

        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', selectedFile.type || 'application/octet-stream');
        xhr.send(selectedFile);
      });

      setUploadProgress(90);
      setUploadStatus('Confirming upload...');

      const confirmResponse = await api.post(
        `/repositories/uploads/${uploadId}/confirm`,
        {},
        { signal },
      );
      const result = confirmResponse.data;

      setUploadProgress(95);
      setUploadStatus('Starting analysis...');

      const analysisResponse = await api.post(
        '/analysis/repository',
        {
          repositoryId: result.repositoryId,
          repositoryName: result.repositoryName,
          platform: 'upload',
          socketId: options.socketId,
        },
        { signal },
      );

      setUploadProgress(100);
      setUploadStatus('Upload complete!');

      if (analysisResponse.data.jobId) {
        navigate(`/jobs/${analysisResponse.data.jobId}`);
      } else {
        throw new Error('Failed to start analysis - no job ID returned');
      }
    } catch (err: unknown) {
      logger.error('Upload error', { error: err });

      if (
        err instanceof Error &&
        (err.name === 'AbortError' || err.message === 'Upload cancelled')
      ) {
        return;
      }

      let errorMessage = 'Upload failed. Please try again.';
      if (axios.isAxiosError(err)) {
        errorMessage = err.response?.data?.error || err.message;
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setIsUploading(false);
      setUploadProgress(0);
      setUploadStatus('');
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleCancelUpload = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsUploading(false);
    setUploadProgress(0);
    setUploadStatus('');
    setError('Upload cancelled');
  };

  const clearSelectedFile = () => setSelectedFile(null);

  return {
    selectedFile,
    isUploading,
    uploadProgress,
    uploadStatus,
    error,
    setError,
    handleFileSelect,
    handleDrop,
    handleDragOver,
    handleUpload,
    handleCancelUpload,
    clearSelectedFile,
  };
}
